import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../../components/PublicLayout";
import { SITE } from "../../content/site";
import { micro } from "../../lib/microcopy";
import { routePath, SITE_URL } from "../../lib/routes";
import { orgRef } from "../../components/Seo";
import { track, EVENTS } from "../../lib/analytics";

const IMG = {
  hero: "/events/hero.jpg", madrid: "/events/madrid.jpg", antwerp: "/events/antwerp.jpg",
  usa: "/events/usa.jpg", sponsorship: "/events/sponsorship.jpg", formats: "/events/formats.jpg",
};

const Overline = ({ children, dark }) => (
  <p className={`overline mb-4 ${dark ? "text-white/40" : "text-[#9a9a9a]"}`}>{children}</p>
);

function HeroVideo({ alt }) {
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return (
    <>
      <img src="/events/hero-poster.jpg" alt={alt} width="854" height="480" fetchPriority="high"
        className="absolute inset-0 h-full w-full object-cover" />
      {!reduced && (
        <video muted playsInline autoPlay loop preload="auto" poster="/events/hero-poster.jpg"
          data-testid="events-hero-video"
          className="absolute inset-0 h-full w-full object-cover">
          <source src="/videos/events-hero.webm" type="video/webm" />
          <source src="/videos/events-hero.mp4" type="video/mp4" />
        </video>
      )}
    </>
  );
}

export default function Events({ lang }) {
  const c = SITE[lang].events;
  useEffect(() => { window.scrollTo(0, 0); track(EVENTS.PAGE_VIEW, { page: "events", lang }); }, [lang]);

  const ld = [{
    "@context": "https://schema.org", "@type": "Service",
    name: "Equestrian event strategy, production and technology",
    serviceType: "Equestrian event management",
    url: `${SITE_URL}${routePath("events", lang)}`,
    description: c.sub, provider: orgRef(), areaServed: "Worldwide", inLanguage: lang,
    image: `${SITE_URL}/og/og-events.jpg`,
  }];

  return (
    <PublicLayout pageKey="events" lang={lang} jsonLd={ld} overlayNav
      crumbs={[{ name: SITE[lang].breadcrumbs.events, path: routePath("events", lang) }]}>
      <div data-testid="events-page">

        {/* Cinematic video hero — fades into the next section */}
        <section className="relative overflow-hidden bg-white" data-testid="events-hero">
          <div className="relative h-[94vh] min-h-[640px] md:h-[116vh] md:min-h-[860px]">
            <HeroVideo alt={c.alt.hero} />
            {/* localized legibility scrim, bottom-left only */}
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(110%_85%_at_0%_70%,rgba(5,5,5,0.55)_0%,rgba(5,5,5,0.22)_40%,transparent_65%)]" />
            {/* long progressive fade to white */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[34%] bg-gradient-to-b from-transparent via-white/70 to-white md:h-[38%]" />

            <div className="relative z-10 mx-auto flex h-full max-w-[1200px] flex-col justify-center px-6 pt-[2vh] md:px-8">
              <p className="overline mb-5 text-white/75 [text-shadow:0_1px_16px_rgba(0,0,0,0.5)]">{c.overline}</p>
              <h1 className="font-display max-w-3xl text-4xl font-light leading-[1.05] tracking-tight text-white [text-shadow:0_2px_28px_rgba(0,0,0,0.5)] sm:text-5xl lg:text-6xl">{c.h1}</h1>
              <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-white/85 [text-shadow:0_1px_18px_rgba(0,0,0,0.55)] md:text-lg">{c.sub}</p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to={routePath("book", lang)} data-testid="events-cta-primary"
                  onClick={() => track(EVENTS.CTA_CLICK, { cta: "events_hero_build" })}
                  className="rounded-full bg-white px-7 py-3.5 text-sm font-medium text-[#050505] shadow-[0_8px_30px_rgba(0,0,0,0.25)] transition-opacity hover:opacity-85">
                  {c.ctaPrimary}
                </Link>
                <a href="#experience" data-testid="events-cta-secondary"
                  className="rounded-full border border-white/45 bg-black/10 px-7 py-3.5 text-sm font-light text-white backdrop-blur-sm transition-colors hover:border-white/80">
                  {c.ctaSecondary}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Experience — rises into the hero's fade zone */}
        <section id="experience" className="relative z-10 mx-auto -mt-[16vh] max-w-[1200px] scroll-mt-20 px-6 pb-24 md:-mt-[22vh] md:px-8 md:pb-32" data-testid="events-experience">
          <Overline>{c.experience.overline}</Overline>
          <h2 className="font-display max-w-3xl text-3xl font-light leading-tight tracking-tight text-[#050505] sm:text-4xl lg:text-5xl">{c.experience.title}</h2>
          <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-[#4a4a4a] md:text-lg">{c.experience.text}</p>
          <p data-testid="events-attribution" className="mt-4 max-w-2xl text-sm font-light leading-relaxed text-[#9a9d98]">{(SITE[lang].eventsHub || SITE.en.eventsHub).attribution}</p>
          <div className="mt-16 space-y-0">
            {c.experience.items.map((it, i) => (
              <article key={i} data-testid={`events-credential-${i}`}
                className="grid gap-4 border-t border-black/8 py-10 md:grid-cols-[320px_1fr] md:gap-14">
                <p className="text-sm font-medium text-[#0e7c6b]">{it.label}</p>
                <div>
                  <h3 className="font-display text-xl font-normal leading-snug text-[#050505] md:text-2xl">{it.title}</h3>
                  <p className="mt-4 max-w-2xl text-base font-light leading-relaxed text-[#6b6f6c]">{it.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Case moment: Madrid */}
        <CaseMoment img={IMG.madrid} alt={c.alt.madrid} w="1920" h="1283" label={c.cases[0].label} title={c.cases[0].title} testid="events-case-madrid" />

        {/* Case moment: Antwerp */}
        <CaseMoment img={IMG.antwerp} alt={c.alt.antwerp} w="1920" h="830" label={c.cases[1].label} title={c.cases[1].title} testid="events-case-antwerp" />

        {/* Case moment: USA */}
        <CaseMoment img={IMG.usa} alt={c.alt.usa} w="1920" h="1280" label={c.cases[2].label} title={c.cases[2].title} testid="events-case-usa" />

        {/* Capabilities band */}
        <section className="relative min-h-[70vh] overflow-hidden" data-testid="events-capabilities-band">
          <img src={IMG.formats} alt={c.alt.formats} loading="lazy" width="1600" height="1074"
            className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative mx-auto flex min-h-[70vh] max-w-[1200px] flex-col items-start justify-center px-6 py-24 md:items-center md:px-8 md:text-center">
            <p className="overline text-white/60">{(SITE[lang].eventsHub || SITE.en.eventsHub).capBand.overline}</p>
            <h2 className="font-display mt-5 max-w-3xl text-3xl font-light leading-tight tracking-tight text-white sm:text-4xl lg:text-6xl">{(SITE[lang].eventsHub || SITE.en.eventsHub).capBand.title}</h2>
            <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-white/75 md:text-lg">{(SITE[lang].eventsHub || SITE.en.eventsHub).capBand.text}</p>
            <Link to={routePath("eventsCapabilities", lang)} data-testid="events-capabilities-cta"
              onClick={() => track(EVENTS.CTA_CLICK, { cta: "events_capabilities" })}
              className="mt-9 inline-block rounded-full bg-white px-8 py-3.5 text-sm font-medium text-[#050505] transition-opacity hover:opacity-85">
              {(SITE[lang].eventsHub || SITE.en.eventsHub).capBand.cta}
            </Link>
          </div>
        </section>

        {/* Numbers (dark) */}
        <section className="bg-[#0a0a0b] px-6 py-24 md:px-8 md:py-28" data-testid="events-numbers">
          <div className="mx-auto max-w-[1200px]">
            <Overline dark>{c.numbers.overline}</Overline>
            <div className="mt-10 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
              {c.numbers.items.map((n, i) => (
                <div key={i} data-testid={`events-number-${i}`}>
                  <p className="font-display text-5xl font-extralight tracking-tight text-white lg:text-6xl">{n.v}</p>
                  <p className="mt-3 max-w-[220px] text-sm font-light leading-relaxed text-white/55">{n.l}</p>
                </div>
              ))}
            </div>
            <p className="mt-14 text-xs font-light text-white/35">{c.numbers.note}</p>
          </div>
        </section>

        {/* Event portfolio */}
        <section className="mx-auto max-w-[1200px] px-6 py-24 md:px-8 md:py-32" data-testid="events-portfolio">
          <Overline>{(SITE[lang].eventsHub || SITE.en.eventsHub).upcoming.overline}</Overline>
          <h2 className="font-display max-w-3xl text-3xl font-light leading-tight tracking-tight text-[#050505] sm:text-4xl lg:text-5xl">{(SITE[lang].eventsHub || SITE.en.eventsHub).upcoming.title}</h2>
          <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-[#4a4a4a] md:text-lg">{(SITE[lang].eventsHub || SITE.en.eventsHub).upcoming.text}</p>
          <Link to={routePath("contact", lang)} data-testid="events-portfolio-cta"
            className="mt-8 inline-block text-sm font-medium text-[#0e7c6b] transition-opacity hover:opacity-75">
            {(SITE[lang].eventsHub || SITE.en.eventsHub).upcoming.cta} →
          </Link>
        </section>

        {/* Final CTA */}
        <section className="mx-auto max-w-[1200px] px-6 py-24 text-center md:px-8 md:py-36" data-testid="events-final-cta">
          <h2 className="font-display mx-auto max-w-3xl text-3xl font-light leading-tight tracking-tight text-[#050505] sm:text-4xl lg:text-5xl">{c.finalCta.title}</h2>
          <p className="mx-auto mt-5 max-w-xl text-base font-light leading-relaxed text-[#4a4a4a] md:text-lg">{c.finalCta.text}</p>
          <Link to={routePath("book", lang)} data-testid="events-final-cta-btn"
            onClick={() => track(EVENTS.CTA_CLICK, { cta: "events_final_talk" })}
            className="mt-10 inline-block rounded-full bg-[#050505] px-9 py-4 text-sm font-medium text-white transition-opacity hover:opacity-85">
            {c.finalCta.cta}
          </Link>
        </section>
      </div>
    </PublicLayout>
  );
}

const CaseMoment = ({ img, alt, label, title, testid, w = "1920", h = "1280" }) => (
  <section className="relative min-h-[60vh] overflow-hidden md:min-h-[76vh]" data-testid={testid}>
    <img src={img} alt={alt} loading="lazy" width={w} height={h}
      className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
    <div className="relative mx-auto flex min-h-[60vh] max-w-[1200px] flex-col justify-end px-6 pb-14 md:min-h-[76vh] md:px-8 md:pb-20">
      <p className="overline mb-4 text-white/55">{label}</p>
      <h2 className="font-display max-w-3xl text-2xl font-light leading-snug tracking-tight text-white sm:text-3xl lg:text-4xl">{title}</h2>
    </div>
  </section>
);
